/**
 * Service + City landing page template.
 *
 * URL pattern:  /:service-:city   (e.g. /car-transport-london, /man-and-van-leeds)
 *
 * Word-count target: 1200-1800 (intro + deepDive + sections + FAQs typically deliver ~1500).
 */
import { useMemo } from "react";
import { SeoNav, SeoFooter } from "./shared/Chrome";
import { SeoHelmet } from "./shared/SeoHelmet";
import {
  Breadcrumbs,
  SeoHero,
  IntroSection,
  HowItWorks,
  WhyChoose,
  AreasCovered,
  FaqSection,
  RelatedServices,
  RelatedLocations,
  CtaSection,
} from "./shared/Sections";
import {
  buildBreadcrumbSchema,
  buildServiceSchema,
  buildLocalBusinessSchema,
  buildFaqSchema,
  canonicalFor,
} from "../lib/schema";
import { CITIES } from "../data/cities";
import { getServicesBySilo, SILOS } from "../data/services";

export default function ServiceLocationTemplate({ service, city }) {
  const pageUrl = `/${service.slug}-${city.slug}`;
  const canonical = canonicalFor(pageUrl);

  // Service siblings (same silo) for related services
  const siblings = useMemo(
    () => getServicesBySilo(service.silo).filter((s) => s.slug !== service.slug).slice(0, 4),
    [service.silo, service.slug],
  );

  // Related cities (other cities offering THIS service)
  const relatedCities = useMemo(
    () => (city.nearbyCities || []).map((slug) => CITIES[slug]).filter(Boolean).slice(0, 8),
    [city],
  );

  // Combined FAQ set (service-level + city-specific tail)
  const faqs = useMemo(
    () => [...service.faqs.slice(0, 5), ...service.localFaqs(city)],
    [service, city],
  );

  // Localised body content
  const localIntro = service.localIntro(city);
  const localDeepDive = `${service.deepDive}\n\nIn ${city.name} specifically, our drivers work daily across postcodes including ${city.postcodeOutwardSamples.slice(0, 5).join(", ")} and reach every borough and neighbourhood across ${city.region}. ${city.name} has its own transport realities — ${city.transportContext} — and we plan jobs around them rather than discovering them on the day.`;

  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: service.name, url: `/${service.slug}` },
    { name: city.name, url: pageUrl },
  ];

  const schemas = [
    buildBreadcrumbSchema(breadcrumbs),
    buildServiceSchema({ service, city, pageUrl }),
    buildLocalBusinessSchema({ city, pageUrl }),
    buildFaqSchema(faqs),
  ];

  return (
    <div className="min-h-screen bg-white" data-testid={`seo-location-page-${service.slug}-${city.slug}`}>
      <SeoHelmet
        title={`${service.name} in ${city.name} | ${city.regionShort} | Transport Anything 247`}
        description={service.metaDescLocal(city)}
        canonical={canonical}
        schemas={schemas}
      />
      <SeoNav />
      <SeoHero
        eyebrow={`${SILOS[service.silo].label} · ${city.name}`}
        h1={service.h1Local(city)}
        lead={localIntro}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        <Breadcrumbs items={breadcrumbs} />
      </div>
      <IntroSection
        heading={`Professional ${service.name} in ${city.name}`}
        body={localDeepDive}
      />
      <HowItWorks steps={service.howItWorks} />
      <WhyChoose
        reasons={service.whyChoose}
        heading={`Why Choose Us for ${service.name} in ${city.name}`}
      />
      <AreasCovered
        heading={`Areas We Cover in ${city.name}`}
        body={`Our ${service.name.toLowerCase()} service in ${city.name} reaches every postcode in the city and the wider ${city.region} area. From central districts to suburban neighbourhoods and the surrounding commuter belt, our drivers know the local access patterns — ${city.transportContext}. Common collection and delivery areas include the neighbourhoods listed here, with same-day collections available across all of them subject to driver availability.`}
        areas={city.neighbourhoods}
      />
      <FaqSection
        faqs={faqs}
        heading={`Frequently Asked Questions about ${service.name} in ${city.name}`}
      />
      <RelatedServices services={siblings} heading={`Related Services in ${city.name}`} />
      <RelatedLocations
        cities={relatedCities}
        serviceSlug={service.slug}
        serviceName={service.name}
        heading={`${service.name} in Cities Near ${city.name}`}
      />
      <CtaSection
        headline={`Book ${service.name.toLowerCase()} in ${city.name}`}
        subhead={`Transparent instant quote in seconds. Same-day collection available across ${city.name} and the surrounding ${city.region}.`}
      />
      <SeoFooter />
    </div>
  );
}
